import { Box, Button, Chip, Stack, Tooltip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { useEffect } from 'react';
import { compact, isEmpty, isNil, map, values } from 'lodash';

import { casingFormat, formatStateFilterLabel } from 'common/utils';
import { getPartnerPractices } from 'store/thunks/partnerThunks';
import { filterPrescriptionsNeedsReview } from 'store/thunks/workQueueThunks';
import { SortKey } from 'enums/common.enum';
import { PrescriptionFormattedStatus } from 'common/constants/prescriptionStatus';

import orderFillType from 'common/constants/orderFillType';
import { tagsChipName } from 'modules/WorkQueue/utils/tagMatchMode';
import FacilityFilterStatusChip from './FacilityFilterStatusChip';

const DRUG_NAME_CHIP_CHAR_LIMIT = 50;

const PrescriptionReviewFilterStatusBar = () => {
  const dispatch = useDispatch();
  const filter = useSelector(({ workQueue }) => workQueue.prescriptionReview?.filter);
  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);

  const formatTag = (tag) => casingFormat(tag?.replace(/_/g, ' '));

  const practices =
    filter?.partnerIds && filter?.practiceIds
      ? compact(
          filter.partnerIds.flatMap((partnerId) => {
            const partner = partners?.find((p) => p.partnerUuid === partnerId);
            return (
              partner?.partnerPractices?.filter((practice) =>
                filter.practiceIds.includes(practice.practiceId)
              ) || []
            );
          })
        )
      : [];

  const practiceName = practices?.map((practice) => casingFormat(practice.practiceName));

  const isPracticeCleared = filter?.practiceIds && !practiceName;

  useEffect(() => {
    if (isPracticeCleared) {
      dispatch(getPartnerPractices({ partnerIds: [filter.partnerIds] }));
    }
  }, [dispatch, isPracticeCleared, filter]);

  const removeFilter = (filterName) => () => {
    dispatch(
      filterPrescriptionsNeedsReview({
        filter: {
          ...filter,
          [filterName]: undefined,
          ...(filterName === 'partnerIds' && { practiceIds: undefined }),
          // POM-6330: a mode outliving its tags would widen the next selection (Urgent included).
          ...(filterName === 'tags' && { tagMatchMode: undefined }),
        },
      })
    );
  };

  const removeAllFilter = () => dispatch(filterPrescriptionsNeedsReview({ filter: {} }));

  return (
    <Stack
      spacing={1}
      direction='row'
      flexWrap='wrap'
      rowGap={1}
      alignItems='center'
      sx={{ maxWidth: '80rem' }}
    >
      <FacilityFilterStatusChip
        removeFilter={removeFilter}
        selectedFacilities={filter?.fulfillingPharmacyIds || []}
      />

      {filter?.drugName && (
        <Chip
          label={
            `Drug Name = ${filter.drugName}`.length > DRUG_NAME_CHIP_CHAR_LIMIT ? (
              <Tooltip title={`Drug Name = ${filter.drugName}`}>
                <Box
                  sx={{
                    maxWidth: '20rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {`Drug Name = ${filter.drugName}`}
                </Box>
              </Tooltip>
            ) : (
              `Drug Name = ${filter.drugName}`
            )
          }
          variant='contained'
          onDelete={removeFilter(SortKey.DRUG_NAME)}
        />
      )}

      {filter?.requestedDate && (
        <Chip
          label={`Requested Date = ${format(new Date(filter.requestedDate), 'MM/dd/yyyy')}`}
          variant='contained'
          onDelete={removeFilter(SortKey.REQUESTED_DATE)}
        />
      )}

      {filter.orderId && (
        <Chip
          label={`Order Id = ${filter.orderId}`}
          variant='contained'
          onDelete={removeFilter(SortKey.ORDER_ID)}
        />
      )}

      {filter.fillType && (
        <Chip
          label={`Fill Type = ${orderFillType[filter.fillType]}`}
          variant='contained'
          onDelete={removeFilter(SortKey.FILL_TYPE)}
        />
      )}

      {filter?.reviewStatus && (
        <Chip
          label={
            <Tooltip
              title={`Review Type =  ${map(
                filter?.reviewStatus,
                (status) => PrescriptionFormattedStatus[status]
              ).join(', ')}`}
            >
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStateFilterLabel(
                  map(filter?.reviewStatus, (status) => PrescriptionFormattedStatus[status]),
                  'Review Type'
                )}
              </Box>
            </Tooltip>
          }
          variant='contained'
          onDelete={removeFilter(SortKey.REVIEW_STATUS)}
        />
      )}

      {filter.tags && (
        <Chip
          key='tags'
          label={
            <Tooltip title={filter.tags.map(formatTag).join(', ')}>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStateFilterLabel(filter.tags.map(formatTag), tagsChipName(filter))}
              </Box>
            </Tooltip>
          }
          variant='contained'
          onDelete={removeFilter('tags')}
          size='small'
        />
      )}

      {filter.tagsNotIn && (
        <Chip
          key='tags-not-in'
          label={
            <Tooltip title={filter.tagsNotIn.map(formatTag).join(', ')}>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStateFilterLabel(filter.tagsNotIn.map(formatTag), 'Tags Not In')}
              </Box>
            </Tooltip>
          }
          variant='contained'
          onDelete={removeFilter('tagsNotIn')}
          size='small'
        />
      )}

      {filter.partnerIds && (
        <Chip
          label={
            <Tooltip
              title={`Partner = ${filter.partnerIds
                .map((partnerId) => {
                  const partner = partners.find((p) => p.partnerUuid === partnerId);
                  return casingFormat(partner?.partnerName || '');
                })
                .join(', ')}`}
            >
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStateFilterLabel(
                  filter.partnerIds.map((partnerId) => {
                    const partner = partners?.find((p) => p?.partnerUuid === partnerId);
                    return casingFormat(partner?.partnerName || '');
                  }),
                  'Partner'
                )}
              </Box>
            </Tooltip>
          }
          key='partners'
          variant='contained'
          onDelete={removeFilter('partnerIds')}
        />
      )}

      {filter?.partnerIds && filter?.practiceIds && (
        <Chip
          label={
            <Tooltip title={practiceName?.join(',')}>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStateFilterLabel(practiceName, 'Practice')}
              </Box>
            </Tooltip>
          }
          variant='contained'
          onDelete={removeFilter('practiceIds')}
        />
      )}

      {!isNil(filter.isReshipment) && (
        <Chip
          label={`Reshipments = ${filter.isReshipment ? 'Yes' : 'No'}`}
          variant='contained'
          onDelete={removeFilter('isReshipment')}
        />
      )}

      {!isEmpty(compact(values(filter))) && <Button onClick={removeAllFilter}>Remove All</Button>}
    </Stack>
  );
};

export default PrescriptionReviewFilterStatusBar;
